import { WorkflowStateTypes } from './../../workflows/model/workflow-state-types.model';
import { Workflows } from './../../workflows/model/workflows.model';
import { StatePermissions } from './model/state-permissions.model';
import { StatePermissionsService } from './service/state-permissions.service';
import { Features } from './../features/model/features.model';
import { FeaturesHierarchy } from './../features/model/features-hierarchy.model';
import { Observable, forkJoin, switchMap, of, catchError, throwError, map } from 'rxjs';
import { MsgModel } from 'src/app/component/common-components/msg/model/msg.model';
import { RolesService } from './../roles/service/roles.service';
import { WorkflowsService } from './../../workflows/service/workflows.service';
import { FeaturesService } from './../features/service/features.service';
import { PermissionsService } from './service/permissions.service';
import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Roles } from '../roles/model/roles.model';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { GetPropertyPipe } from 'src/app/pipes/get-property.pipe';
import { Permissions } from './model/permissions.model';

@Component({
    selector: 'app-permissions',
    templateUrl: './permissions.component.html',
    styleUrls: ['./permissions.component.scss']
})
export class PermissionsComponent implements OnInit {
    @ViewChildren(MatPaginator) paginators: QueryList<MatPaginator> = new QueryList<MatPaginator>();
    @ViewChildren(MatSort) sorts: QueryList<MatSort> = new QueryList<MatSort>();

    showActiveOnly: boolean = true;

    roles: Roles[] = [];
    features: Features[] = [];
    featuresHierarchy: FeaturesHierarchy[] = [];
    permissions: Permissions[] = [];
    statePermissions: StatePermissions[] = [];

    workflows: any[] = [];
    featuresBuilt: any[];

    // Table display
    displayedColumns: string[] = [];
    workflowDisplayedColumns: string[] = [];

    // Table results
    tableDataSources: MatTableDataSource<any>[] = [];

    // Results
    displayName: string = '';
    filter: string = '';
    workflowFilter: string = '';

    isLoading: boolean = false;
    msg: MsgModel = {};

    getPropertyPipe = new GetPropertyPipe();
    stateObs: Observable<any>[] = [];

    constructor(
        private permissionsService: PermissionsService,
        private statePermissionsService: StatePermissionsService,
        private featuresService: FeaturesService,
        private rolesService: RolesService,
        private workflowsService: WorkflowsService
    ) {}

    ngOnInit(): void {
        this.getAll();
    }

    /*
  		Get Functions
  	*/
    getAll() {
        this.isLoading = true;

        let obs: Observable<any>[] = [];
        obs.push(this.getFeatures());
        obs.push(this.getFeaturesHierarchy());
        obs.push(this.getRoles());
        obs.push(this.getPermissions());
        obs.push(this.getStatePermissions());
        obs.push(this.getWorkflows());

        forkJoin(obs).subscribe((res) => {
            forkJoin(this.stateObs).subscribe((res) => {
                this.updateRoleColumns();
                this.buildFeaturesHierarchy();
                this.buildWorkflowStatePermissions();
                this.updateDataSource();

                this.setFilter();

                this.isLoading = false;
            });
        });
    }

    getFeatures() {
        return this.featuresService.indexFeatures().pipe(
            map((res) => {
                if (res.features) {
                    this.features = res.features;
                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving features.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }

    getFeaturesHierarchy() {
        return this.featuresService.indexFeaturesHierarchy().pipe(
            map((res) => {
                if (res.featuresHierarchy) {
                    this.featuresHierarchy = res.featuresHierarchy;
                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving features hierarchy.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }
    getRoles() {
        return this.rolesService.index().pipe(
            map((res) => {
                if (res.roles) {
                    this.roles = res.roles;
                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving roles.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }
    getPermissions() {
        return this.permissionsService.index().pipe(
            map((res) => {
                if (res.permissions) {
                    this.permissions = res.permissions;
                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving permissions.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }
    getStatePermissions() {
        return this.statePermissionsService.index().pipe(
            map((res) => {
                if (res.statePermissions) {
                    this.statePermissions = res.statePermissions;
                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving state permissions.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }
    getWorkflows() {
        return this.workflowsService.index().pipe(
            map((res) => {
                if (res.workflows) {
                    this.workflows = res.workflows;

                    res.workflows.forEach((workflow: Workflows) => {
                        this.stateObs.push(this.getWorkflowStates(workflow));
                    });

                    forkJoin(this.stateObs).subscribe((res2) => {});
                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving state permissions.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }
    getWorkflowStates(workflow: any) {
        return this.workflowsService.getWorkflowStates(workflow.ID).pipe(
            map((res) => {
                if (res.states) {
                    workflow.STATES = res.states;

                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving state permissions.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }

    /*
  		Post Functions
  	*/
    createPermission(role: Roles, feature: Features) {
        this.permissionsService.create(role, feature).subscribe({
            next: (res: any) => {
                if (res.permission) {
                } else if (res.msg) {
                    this.msg = res.msg;
                } else {
                    this.msg = { SUCCESS: false, MSG: 'Error creating permission.' };
                }
            },
            error: (err: any) => {
                this.msg = { SUCCESS: false, MSG: 'Error creating permission.' };
            },
            complete: () => {}
        });
    }

    deletePermission(role: Roles, feature: Features) {
        this.permissionsService.deletePermission(role, feature).subscribe({
            next: (res: any) => {
                if (res.msg) {
                    this.msg = res.msg;
                } else {
                    this.msg = { SUCCESS: false, MSG: 'Error deleting permission.' };
                }
            },
            error: (err: any) => {
                this.msg = { SUCCESS: false, MSG: 'Error deleting permission.' };
            },
            complete: () => {}
        });
    }

    createWorkflowStatePermission(role: Roles, state: WorkflowStateTypes) {
        this.statePermissionsService.create(role, state).subscribe({
            next: (res: any) => {
                if (res.permission) {
                } else if (res.msg) {
                    this.msg = res.msg;
                } else {
                    this.msg = { SUCCESS: false, MSG: 'Error creating workflow state permission.' };
                }
            },
            error: (err: any) => {
                this.msg = { SUCCESS: false, MSG: 'Error creating workflow state permission.' };
            },
            complete: () => {}
        });
    }

    deleteWorkflowStatePermission(role: Roles, state: WorkflowStateTypes) {
        this.statePermissionsService.deleteStatePermission(role, state).subscribe({
            next: (res: any) => {
                if (res.msg) {
                    this.msg = res.msg;
                } else {
                    this.msg = { SUCCESS: false, MSG: 'Error deleting workflow state permission.' };
                }
            },
            error: (err: any) => {
                this.msg = { SUCCESS: false, MSG: 'Error deleting workflow state permission.' };
            },
            complete: () => {}
        });
    }

    /*
  		Build Functions
  	*/
    updateRoleColumns() {
        this.displayedColumns = ['NAME'];
        this.roles.forEach((role) => {
            this.displayedColumns.push(role.NAME!);
        });
    }

    buildFeaturesHierarchy() {
        this.featuresBuilt = [];

        this.featuresHierarchy.forEach((fh) => {
            let parentFeatureIndex = this.featuresBuilt.findIndex((fb) => fb.ID === fh.FEATURES_ID_SUPER);

            if (parentFeatureIndex >= 0) {
                let childFeature: any = this.features.find((f) => f.ID === fh.FEATURES_ID_SUB);

                this.roles.forEach((role) => {
                    childFeature[role.ID!] = this.hasPermission(childFeature.ID, role.ID!);
                });

                this.featuresBuilt[parentFeatureIndex].CHILDREN.push(childFeature);
            } else {
                let parentFeature: any = this.features.find((f) => f.ID === fh.FEATURES_ID_SUPER);
                let childFeature: any = this.features.find((f) => f.ID === fh.FEATURES_ID_SUB);

                this.roles.forEach((role) => {
                    childFeature[role.ID!] = this.hasPermission(childFeature.ID, role.ID!);
                });

                parentFeature.CHILDREN = [];
                parentFeature.CHILDREN.push(childFeature);

                this.featuresBuilt.push(parentFeature);
            }
        });
    }

    buildWorkflowStatePermissions() {
        this.workflows.forEach((workflow: any) => {
            workflow.STATES.forEach((state: any) => {
                this.roles.forEach((role) => {
                    state[role.ID!] = this.hasWorkflowStatePermission(state.ID, role.ID!);
                });
            });
        });
    }

    updateDataSource() {
        this.tableDataSources = [];

        this.featuresBuilt.forEach((parentFeature, index) => {
            // Data
            let dataSource = new MatTableDataSource<any>();
            dataSource.data = parentFeature.CHILDREN.sort((n1: any, n2: any) => (n1.NAME! > n2.NAME! ? 1 : -1));

            // Paginator
            //dataSource.paginator = this.paginators.toArray()[0];

            // Sort
            // dataSource.sort = this.sorts.toArray()[index];
            // const defaultSort: Sort = { active: 'NAME', direction: 'asc' };
            // dataSource.sort.active = defaultSort.active;
            // dataSource.sort.direction = defaultSort.direction;
            // dataSource.sort.sortChange.emit(defaultSort);
            dataSource.sortingDataAccessor = (obj, property) => this.getPropertyPipe.transform(obj, property, true);

            // Filter
            dataSource.filterPredicate = (data: any, filter) => {
                if (this.showActiveOnly) {
                    if (data.ACTIVE) {
                        const dataStr = JSON.stringify(data).toLowerCase();
                        return dataStr.indexOf(filter) != -1;
                    } else {
                        return false;
                    }
                } else {
                    const dataStr = JSON.stringify(data).toLowerCase();
                    return dataStr.indexOf(filter) != -1;
                }
            };

            //this.tableDataSources.push(dataSource);
            parentFeature.tableDataSource = dataSource;
        });

        this.workflows.forEach((workflow, index) => {
            // Data
            let dataSource = new MatTableDataSource<any>();
            dataSource.data = workflow.STATES.sort((n1: any, n2: any) => (n1.NAME! > n2.NAME! ? 1 : -1));

            // Paginator
            //dataSource.paginator = this.paginators.toArray()[0];

            // Sort
            // dataSource.sort = this.sorts.toArray()[index];
            // const defaultSort: Sort = { active: 'NAME', direction: 'asc' };
            // dataSource.sort.active = defaultSort.active;
            // dataSource.sort.direction = defaultSort.direction;
            // dataSource.sort.sortChange.emit(defaultSort);
            dataSource.sortingDataAccessor = (obj, property) => this.getPropertyPipe.transform(obj, property, true);

            // Filter
            dataSource.filterPredicate = (data: any, filter) => {
                const dataStr = JSON.stringify(data).toLowerCase();
                return dataStr.indexOf(filter) != -1;
            };

            //this.tableDataSources.push(dataSource);
            workflow.tableDataSource = dataSource;
        });
    }

    setFilter() {
        this.featuresBuilt.forEach((parentFeature, index) => {
            if (this.filter.length === 0) {
                parentFeature.tableDataSource.filter = 'active';
            } else {
                parentFeature.tableDataSource.filter = this.filter.trim().toLocaleLowerCase();
            }
        });
    }

    setWorkflowFilter() {
        this.workflows.forEach((workflow, index) => {
            workflow.tableDataSource.filter = this.workflowFilter.trim().toLocaleLowerCase();
        });
    }

    openGroup(parentFeatureId: number) {
        this.featuresBuilt[parentFeatureId].isOpen = true;
    }

    closeGroup(parentFeatureId: number) {
        this.featuresBuilt[parentFeatureId].isOpen = false;
    }

    openWorkflowGroup(workflowId: number) {
        this.workflows[workflowId].isOpen = true;
    }

    closeWorkflowGroup(workflowId: number) {
        this.workflows[workflowId].isOpen = false;
    }

    togglePermission(event: any, role: Roles, feature: Features) {
        if (event.checked) {
            this.createPermission(role, feature);
        } else {
            this.deletePermission(role, feature);
        }
    }

    toggleWorkflowStatePermission(event: any, role: Roles, state: WorkflowStateTypes) {
        if (event.checked) {
            this.createWorkflowStatePermission(role, state);
        } else {
            this.deleteWorkflowStatePermission(role, state);
        }
    }

    /*
		Find
	*/
    hasPermission(featureId: number, roleId: number) {
        return this.permissions.findIndex((permission: Permissions) => permission.FEATURES_ID === featureId && permission.ROLES_ID === roleId) >= 0;
    }

    hasWorkflowStatePermission(stateId: number, roleId: number) {
        return (
            this.statePermissions.findIndex(
                (permission: StatePermissions) => permission.WORKFLOW_STATE_TYPES_ID === stateId && permission.ROLES_ID === roleId
            ) >= 0
        );
    }
}
